import { useState, useCallback } from 'react';
import { generateLanguageMessage, checkForLanguageSwitch, handleLanguageDetectedEvent } from '@/utils/chatLanguageHelpers';
import { sendChatMessage, gatherWebsiteAnalysisData } from '@/utils/chatStreamingHelpers';
import { generateOutlineForIdea } from '@/utils/chatOutlineHelpers';
import { supabase } from '@/integrations/supabase/client';
import type { PostIdea } from '@/components/chat/IdeaCards';

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
}

export interface UseSharedChatLogicProps {
  currentWebsite?: { id: string; url: string };
  websiteId: string;
  enableLanguageDetection?: boolean;
}

export const useSharedChatLogic = ({ currentWebsite, websiteId, enableLanguageDetection = false }: UseSharedChatLogicProps) => {
  const [hasShownLanguageMessage, setHasShownLanguageMessage] = useState(false);

  // Language detection functions (only active when enableLanguageDetection is true)
  const checkLanguageDetection = useCallback(async (setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void) => {
    if (!enableLanguageDetection) return;
    
    const websiteLanguage = checkForLanguageSwitch();
    if (websiteLanguage && !hasShownLanguageMessage) {
      const message = await generateLanguageMessage(websiteLanguage, websiteId, currentWebsite?.url);
      setMessages(prev => [...prev, { role: 'assistant', content: message }]);
      setHasShownLanguageMessage(true);
    }
  }, [hasShownLanguageMessage, websiteId, currentWebsite, enableLanguageDetection]);

  const setupLanguageDetection = useCallback((setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void) => {
    if (!enableLanguageDetection) return () => {};
    
    const handleLanguageDetected = async (event: CustomEvent) => {
      const message = await handleLanguageDetectedEvent(event, hasShownLanguageMessage);
      if (message) {
        setMessages(prev => [...prev, { role: 'assistant', content: message }]);
        setHasShownLanguageMessage(true);
      }
    };

    window.addEventListener('language-detected', handleLanguageDetected as EventListener);
    return () => window.removeEventListener('language-detected', handleLanguageDetected as EventListener);
  }, [hasShownLanguageMessage, enableLanguageDetection]);

  // Send message using shared streaming logic
  const sendMessage = useCallback(async (
    message: string,
    messages: ChatMessage[],
    setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void,
    setSending: (sending: boolean) => void
  ) => {
    // Always use gatherWebsiteAnalysisData for onboarding chat (reads from localStorage)
    // Main app chat will override this by passing custom data
    const websiteAnalysisData = gatherWebsiteAnalysisData();

    await sendChatMessage({
      input: message,
      messages,
      currentWebsite,
      websiteAnalysisData,
      setMessages,
      setSending
    });
  }, [currentWebsite]);

  // Handle create outline - direct wrapper around existing helper
  const handleCreateOutline = useCallback(async (
    idea: PostIdea,
    messages: ChatMessage[],
    setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void,
    setInput: (input: string) => void,
    setSending: (sending: boolean) => void
  ) => {
    await generateOutlineForIdea({
      idea,
      currentWebsite,
      messages,
      setMessages,
      setInput,
      setSending
    });
  }, [currentWebsite]);

  return {
    // Language detection (only for onboarding chat)
    checkLanguageDetection,
    setupLanguageDetection,
    
    // Core chat functions
    sendMessage,
    handleCreateOutline,
    
    // State
    hasShownLanguageMessage
  };
};
